import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePopper } from 'react-popper';

import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';

import { hasSeenRedeemCashCoachMark, markRedeemCashCoachMarkSeen } from './helper';

// Gap between the bubble's tail and the top of the footer nav.
const POPPER_OFFSET: [number, number] = [0, 12];
// Keeps the bubble off the screen edges, and keeps the tail off the bubble's own rounded corners.
const OVERFLOW_PADDING = 12;
const ARROW_PADDING = 16;

// Per the data-team Figma spec: parent `redeem`, category `redeem_cash`, one event name carrying both
// the impression and the CTA click. Goes through sendRedeemEvent because the redeem-redesign taxonomy
// isn't keyed in @sunflower-games/analytics-events yet.
const ANALYTICS_EVENT = 'popup_redeem_cash_area';

// Mirrors the `landscape-or-desktop` mixin (src/styles/mixins.scss) that sets `display: none` on
// .footerNavigation. That is CSS only — the footer still MOUNTS on desktop and these hooks still run,
// so without this gate the coach-mark would burn its once-per-player flag while rendering into a
// hidden parent, and the player would never see it on any device. Kept reactive (useMediaQuery
// subscribes to changes) so a player who rotates to portrait, or narrows the window, still gets it
// while the pending flag is up. If that mixin's breakpoint changes, change this with it.
const FOOTER_HIDDEN_QUERY = '(orientation: landscape), (min-width: 769px)';

interface UseRedeemCashCoachMarkParams {
  // Caller-owned gate: the Redeem tab must actually exist and the nav must actually render.
  // FooterNavigation runs its hooks BEFORE `if (hideNavigationBar) return null`, so without this the
  // hook could burn the once-only seen-flag on a frame that renders nothing.
  enabled: boolean;
  onRedeem: () => void;
}

export function useRedeemCashCoachMark({ enabled, onRedeem }: UseRedeemCashCoachMarkParams) {
  const { crownRipsStore, uiStore, userStore, authStore, animationStore, mysteryStore } = useStores();
  const isFlagEnabled = useFlag('crown_rips_redeem_coachmark_enabled', false);
  const isFooterHidden = useMediaQuery(FOOTER_HIDDEN_QUERY);

  const [isOpen, setIsOpen] = useState(false);
  const hasOpenedRef = useRef(false);

  // The anchor lives in a ref, and is promoted into popper state only at the moment of display (in the
  // effect below). A `ref` callback that writes state directly would make every mount of the footer —
  // for every player, flag on or off — pay an extra synchronous render pass of the whole nav subtree,
  // to position a bubble that almost never appears.
  const anchorRef = useRef<HTMLElement | null>(null);
  const setReferenceElement = useCallback((el: HTMLElement | null) => {
    anchorRef.current = el;
  }, []);

  const [referenceElement, setPopperReference] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);

  // Memoised so react-popper's own options memo actually hits: it lists `modifiers` in its deps, so a
  // fresh array literal here would re-run its factory and a full deep-compare of the options tree on
  // every footer render.
  const modifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: POPPER_OFFSET } },
      { name: 'preventOverflow', options: { padding: OVERFLOW_PADDING } },
      { name: 'arrow', options: { element: arrowElement, padding: ARROW_PADDING } },
      // Popper listens to scroll by default and each tick reaches us as a ReactDOM.flushSync — a
      // forced synchronous re-render of the ENTIRE footer (every tab, the animations, the dots),
      // during the few seconds the bubble is up and the player is most likely to be scrolling. It
      // buys nothing here: both the anchor and the bubble live inside the position:fixed footer, so
      // their relative position can't change on scroll. Resize still matters, for rotation.
      { name: 'eventListeners', options: { scroll: false, resize: true } },
    ],
    [arrowElement],
  );

  const { styles } = usePopper(referenceElement, popperElement, { placement: 'top', modifiers });

  // Everything EXCEPT the "already seen" check — that one lives in the effect below, because reading
  // it in the same expression that writes it would be self-cancelling: the bubble would mark itself
  // seen and disappear on the frame it appeared.
  const eligible =
    isFlagEnabled &&
    enabled &&
    !isFooterHidden &&
    authStore.authorized &&
    crownRipsStore.redeemCoachMarkPending &&
    !uiStore.isDialogOpen &&
    !animationStore.animationBlockScreenVisible &&
    !mysteryStore.isRevealing;

  useEffect(() => {
    // The uid is read here rather than during render on purpose: `userStore.profile` is replaced
    // wholesale on every profile write (language, KYC, tutorial flags…), so touching it in the render
    // pass would subscribe this observer footer to all of them. Effect bodies aren't tracked.
    const firebaseUid = userStore.profile?.firebaseUid || '';
    if (!eligible || !firebaseUid || hasOpenedRef.current || hasSeenRedeemCashCoachMark(firebaseUid)) {
      return;
    }
    // Burn the once-only flag at the moment of DISPLAY, not on dismiss: a refresh while the bubble is
    // up must not re-trigger it. `hasOpenedRef` keeps a re-render from re-entering here.
    hasOpenedRef.current = true;
    markRedeemCashCoachMarkSeen(firebaseUid);
    setPopperReference(anchorRef.current);
    setIsOpen(true);
    void analytics.sendRedeemEvent(ANALYTICS_EVENT, {
      event_parent: 'redeem',
      event_category: 'redeem_cash',
      event_action: 'impression',
    });
  }, [eligible, userStore]);

  // `eligible` is re-checked rather than a hand-picked subset of it, so anything that invalidates the
  // coach-mark while it is up — a dialog opening on its own, the kill switch flipping — hides it
  // without waiting for a tap. Safe because nothing clears `redeemCoachMarkPending` except `dismiss`.
  const showCoachMark = isOpen && eligible;

  const dismiss = useCallback(() => {
    setIsOpen(false);
    crownRipsStore.dismissRedeemCoachMark();
  }, [crownRipsStore]);

  const handleRedeemClick = useCallback(() => {
    void analytics.sendRedeemEvent(ANALYTICS_EVENT, {
      event_parent: 'redeem',
      event_category: 'redeem_cash',
      event_action: 'click',
    });
    dismiss();
    onRedeem();
  }, [dismiss, onRedeem]);

  // Non-blocking dismissal: no backdrop, so the tap still reaches whatever is underneath. Tapping the
  // Redeem tab itself therefore both opens Redeem and dismisses — the same end state as the CTA.
  useEffect(() => {
    if (!showCoachMark) {
      return;
    }
    const handlePointerDown = (event: Event) => {
      if (popperElement?.contains(event.target as Node)) {
        return;
      }
      dismiss();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showCoachMark, popperElement, dismiss]);

  return {
    showCoachMark,
    setReferenceElement,
    setPopperElement,
    setArrowElement,
    popperStyles: styles.popper,
    arrowStyles: styles.arrow,
    handleRedeemClick,
  };
}
